import { useState } from 'react';

import { Suspense } from '@sorare/core/src/atoms/Suspense';
import { Backdrop } from '@sorare/core/src/atoms/loader/Backdrop';
import { Dialog } from '@sorare/core/src/components/dialog';
import { JonumRewardGateProvider } from '@sorare/core/src/components/jonum/JonumRewardGateProvider';
import {
  JONUM_GATE_DIALOG_PARAM,
  JONUM_REWARD_GATE_DIALOG_PARAM,
  useJonumVerificationGuard,
} from '@sorare/core/src/components/jonum/JonumVerificationDialog/useJonumVerificationGuard';
import {
  DialogKey,
  WithDialogs,
} from '@sorare/core/src/components/navigation/WithDialogs';
import { DepositAcceptedDialog } from '@sorare/core/src/components/wallet/BankEthAccounting/AddFundsToFiatWallet/DepositAcceptedDialog';
import { useCloseDialog } from '@sorare/core/src/hooks/navigation/useCloseDialog';
import { useDialogParam } from '@sorare/core/src/hooks/navigation/useDialogParam';
import { lazy } from '@sorare/core/src/lib/retry';
import { RequireAuth } from '@sorare/core/src/routing/RequireAuth';
import { canBuyPack } from '@sorare/marketplace/src/components/commonCard/PackDetailsDialog/canBuyPack.ts';

//core
const CreditClaimedDialog = lazy(
  async () =>
    import('@sorare/core/src/components/conversionCredit/CreditClaimedDialog'),
  'CreditClaimedDialog'
);
const VerifyPhoneNumber = lazy(
  async () => import('@sorare/core/src/components/user/VerifyPhoneNumber'),
  'VerifyPhoneNumber'
);
const UnblockWallet = lazy(
  async () => import('@sorare/core/src/components/fiatWallet/UnblockWallet'),
  'UnblockWallet'
);
const JoinConnectedTaskDialog = lazy(
  async () =>
    import('@sorare/core/src/components/missions/JoinConnectedTaskDialog'),
  'JoinConnectedTaskDialog'
);
const JonumVerificationDialog = lazy(
  async () =>
    import('@sorare/core/src/components/jonum/JonumVerificationDialog/index.tsx'),
  'JonumVerificationDialog'
);
const JonumKycPromptDialog = lazy(
  async () =>
    import('@sorare/core/src/components/jonum/JonumVerificationDialog/JonumKycPromptDialog.tsx'),
  'JonumKycPromptDialog'
);

// marketplace
const AuctionDialog = lazy(
  async () =>
    import('@sorare/marketplace/src/components/buyActions/BidField/BidPaymentFlow'),
  'BidPaymentFlow'
);
const PrimaryOfferDialog = lazy(
  async () =>
    import('@sorare/marketplace/src/components/primaryBuy/PrimaryBuyBuyField/PrimaryOfferBuyFieldWrapper/StandalonePrimaryBuyFlow'),
  'StandalonePrimaryBuyFlow'
);
const Checkout = lazy(
  async () => import('@sorare/marketplace/src/components/cart/Checkout'),
  'Checkout'
);
const RoutedNewSaleDialog = lazy(
  async () =>
    import('@sorare/marketplace/src/components/offer/RoutedSaleDialog'),
  'RoutedNewSaleDialog'
);

const BuyGemsDialog = lazy(
  async () =>
    import('@sorare/marketplace/src/components/commonCard/BuyGemsDialog'),
  'BuyGemsDialog'
);
const WelcomePackDialog = lazy(
  async () =>
    import('@sorare/marketplace/src/components/commonCard/WelcomePackDialog'),
  'WelcomePackDialog'
);

const BuyCardPackDialog = lazy(
  async () =>
    import('@sorare/marketplace/src/components/commonCard/BuyPacks/BuyCardPackDialog'),
  'BuyCardPackDialog'
);

const PackDetailsDialog = lazy(
  async () =>
    import('@sorare/marketplace/src/components/commonCard/PackDetailsDialog'),
  'PackDetailsDialog'
);

const BuyPackButton = lazy(
  async () =>
    import('@sorare/marketplace/src/components/commonCard/BuyPackButton'),
  'BuyPackButton'
);

// gameplay
const DeliverableItemOrderFormRoutedDialog = lazy(
  async () =>
    import('#src/components/rewards/deliverableItem/DeliverableItemOrderFormRoutedDialog/index.tsx'),
  'DeliverableItemOrderFormRoutedDialog'
);
const SpecificNewsDialogContent = lazy(
  async () => import('#src/components/activity/SpecificNews/index.tsx'),
  'SpecificNewsDialogContent'
);
const TrckGroupDrawer = lazy(
  async () => import('#src/components/trckGroup/TrckGroupDrawer/index.tsx'),
  'TrckGroupDrawer'
);
const AnyCardPage = lazy(
  async () => import('#src/components/AnyCardPage/index.tsx'),
  'AnyCardPageDialog'
);
const GuessTheScoreDialog = lazy(
  async () =>
    import('#src/components/series/GuessTheScore/GuessTheScoreDialog/index.tsx'),
  'GuessTheScoreDialog'
);

type Props = {
  children: React.ReactNode;
};

const RoutedJonumVerificationDialog = ({
  onClose,
}: {
  onClose: () => void;
}) => {
  const dialogParam = useDialogParam(DialogKey.jonumVerification);
  const openedByRewardGate = dialogParam === JONUM_REWARD_GATE_DIALOG_PARAM;
  const openedByGate =
    dialogParam === JONUM_GATE_DIALOG_PARAM || openedByRewardGate;
  const {
    missingInfo,
    needsClassification,
    onStepComplete,
    shouldShow,
    kycRequired,
    kycUnderReview,
  } = useJonumVerificationGuard({ openedByGate });

  const onlyKycOwed = !shouldShow && kycRequired && !kycUnderReview;
  const [showKycPrompt] = useState(() => openedByRewardGate && onlyKycOwed);

  if (showKycPrompt) {
    return <JonumKycPromptDialog open onDone={onClose} />;
  }

  return (
    <JonumVerificationDialog
      open
      onClose={onClose}
      onComplete={onClose}
      missingInfo={missingInfo}
      showIntro={needsClassification}
      onStepComplete={onStepComplete}
      openedByGate={openedByGate}
    />
  );
};

const RoutedPackDetailsDialog = ({ onClose }: { onClose: () => void }) => {
  const cardPackSlug = useDialogParam(DialogKey.packDetails);

  if (!cardPackSlug) {
    return null;
  }

  return (
    <PackDetailsDialog
      open
      onClose={onClose}
      cardPackSlug={cardPackSlug}
      footer={cardPack =>
        canBuyPack(cardPack) && (
          // Local boundary so loading the button doesn't suspend the dialog.
          <Suspense fallback={null}>
            <BuyPackButton
              cardPack={cardPack}
              fullWidth
              onCardsClaimed={onClose}
            />
          </Suspense>
        )
      }
    />
  );
};

export const DialogsProvider = ({ children }: Props) => {
  const closeDialog = useCloseDialog();

  return (
    <JonumRewardGateProvider>
      <WithDialogs
        dialogs={[
          {
            name: DialogKey.guessTheScoreTask,
            element: (
              <Suspense fallback={<Backdrop />}>
                <GuessTheScoreDialog
                  open
                  onClose={() => closeDialog(DialogKey.guessTheScoreTask)}
                />
              </Suspense>
            ),
          },
          {
            name: DialogKey.jonumVerification,
            element: (
              <Suspense fallback={<Backdrop />}>
                <RoutedJonumVerificationDialog
                  onClose={() => closeDialog(DialogKey.jonumVerification)}
                />
              </Suspense>
            ),
          },
          {
            name: DialogKey.deliveryItemOrder,
            element: (
              <RequireAuth redirectTo="signup">
                <DeliverableItemOrderFormRoutedDialog />
              </RequireAuth>
            ),
          },
          {
            name: DialogKey.news,
            element: (
              <Suspense fallback={<Backdrop />}>
                <Dialog
                  open
                  maxWidth="xs"
                  fullWidth
                  onClose={() => closeDialog(DialogKey.news)}
                >
                  <SpecificNewsDialogContent />
                </Dialog>
              </Suspense>
            ),
          },
          {
            name: DialogKey.paypalTransactionId,
            element: (
              <Suspense fallback={<Backdrop />}>
                <DepositAcceptedDialog />
              </Suspense>
            ),
          },
          {
            name: DialogKey.claimedConversionCredit,
            element: (
              <Suspense fallback={<Backdrop />}>
                <CreditClaimedDialog />
              </Suspense>
            ),
          },
          {
            name: DialogKey.card,
            element: (
              <Dialog
                open
                fullScreen
                hideHeader
                skipTransition
                onClose={() => closeDialog(DialogKey.card)}
              >
                <Suspense fallback={<Backdrop />}>
                  <AnyCardPage />
                </Suspense>
              </Dialog>
            ),
          },
          {
            name: DialogKey.auction,
            element: (
              <Suspense fallback={<Backdrop />}>
                <AuctionDialog />
              </Suspense>
            ),
          },
          {
            name: DialogKey.primaryOffer,
            element: (
              <Suspense fallback={<Backdrop />}>
                <PrimaryOfferDialog />
              </Suspense>
            ),
          },
          {
            name: DialogKey.unblockWallet,
            element: (
              <Suspense fallback={<Backdrop />}>
                <UnblockWallet />
              </Suspense>
            ),
          },
          {
            name: DialogKey.buyGems,
            element: (
              <Suspense fallback={<Backdrop />}>
                <BuyGemsDialog />
              </Suspense>
            ),
          },
          {
            name: DialogKey.verifyPhone,
            element: (
              <Suspense fallback={<Backdrop />}>
                <VerifyPhoneNumber
                  onCancel={() => closeDialog(DialogKey.verifyPhone)}
                />
              </Suspense>
            ),
          },
          {
            name: DialogKey.checkout,
            element: (
              <Suspense fallback={<Backdrop />}>
                <Checkout />
              </Suspense>
            ),
          },
          {
            name: DialogKey.singleSaleOffer,
            element: (
              <Suspense fallback={<Backdrop />}>
                <RoutedNewSaleDialog />
              </Suspense>
            ),
          },
          {
            name: DialogKey.cardPack,
            element: (
              <Suspense fallback={<Backdrop />}>
                <BuyCardPackDialog />
              </Suspense>
            ),
          },
          {
            name: DialogKey.packDetails,
            element: (
              <Suspense fallback={<Backdrop />}>
                <RoutedPackDetailsDialog
                  onClose={() => closeDialog(DialogKey.packDetails)}
                />
              </Suspense>
            ),
          },
          {
            name: DialogKey.competitionDrawer,
            element: (
              <Suspense fallback={<Backdrop />}>
                <TrckGroupDrawer />
              </Suspense>
            ),
          },
          {
            name: DialogKey.connect_campaign,
            element: (
              <Suspense fallback={<Backdrop />}>
                <JoinConnectedTaskDialog />
              </Suspense>
            ),
          },
          {
            name: DialogKey.welcomePack,
            element: <WelcomePackDialog />,
          },
        ]}
      >
        {children}
      </WithDialogs>
    </JonumRewardGateProvider>
  );
};
